// Key resolution for client-side verification. Keys are found by kid, never by
// position, in the DID document version in force at signing time; the
// algorithm must follow the key. Sources, all carried in a verification bundle:
//   did:key        the key is the identifier
//   did:webvh      the log, verified entry by entry (webvh.ts), then the version
//                  at versionTime
//   hosted did:web the rail-defined issuer history (issuer_history.py): each
//                  version ES256-signed by #key-1 of the version before it
// Compromise declarations (did_registry.py) invalidate a key's signatures made
// at or after the stated time; routine revocation does not.
import { sha256 } from "@noble/hashes/sha256";
import { toHex } from "./encoding";
import { canonical } from "./jcs";
import { didKeyPublicKey, fromJwk, fromMultikey, type PublicKey } from "./keys";
import { parseCompact, payloadJson, verifyRaw } from "./jws";
import { select, updateKeyVm, verifyLog, type Version } from "./webvh";

export const COMPROMISE_TYP = "did-key-compromise+jws";
export const HISTORY_TYPE = "QseverseRailIssuerDidHistory";

export interface IssuerHistory {
  did: string; conformsTo?: string;
  entries: { seq: number; prev_hash: string; entry_hash: string; version_time: string;
             document: Record<string, any>; jws: string; kid: string }[];
}
export type DidSource = unknown[] | IssuerHistory;
export interface Compromise { did: string; kid: string; compromisedAfter: string }

const absolute = (doc: Record<string, any>, id: string) => (id.startsWith("#") ? doc.id + id : id);

/** The verification method `kid` in `doc`, if it is listed under `relationship`. */
export function keyFromDocument(doc: Record<string, any>, kid: string,
                                relationship = "assertionMethod"): PublicKey {
  const rel: unknown[] = doc[relationship] ?? [];
  const listed = rel.some((r) => (typeof r === "string" ? absolute(doc, r) : absolute(doc, (r as any).id)) === kid);
  if (!listed) throw new Error(`${kid} is not listed in ${relationship}`);
  const vms = [...(doc.verificationMethod ?? []), ...rel.filter((r) => typeof r === "object")] as any[];
  const vm = vms.find((v) => absolute(doc, v.id) === kid);
  if (!vm) throw new Error(`${kid} is not in the document`);
  if (vm.publicKeyMultibase) return fromMultikey(vm.publicKeyMultibase);
  if (vm.publicKeyJwk) return fromJwk(vm.publicKeyJwk);
  throw new Error(`${kid} has no key material`);
}

/** Verify an issuer history; returns its documents with their version times. */
export function verifyIssuerHistory(h: IssuerHistory): { time: string; document: Record<string, any> }[] {
  let prevHash = "0".repeat(64);
  let prevDoc: Record<string, any> | null = null;
  const out: { time: string; document: Record<string, any> }[] = [];
  h.entries.forEach((e, i) => {
    const m = { v: 1, type: HISTORY_TYPE, did: h.did, seq: e.seq, prev_hash: e.prev_hash,
                version_time: e.version_time, doc_hash: toHex(sha256(canonical(e.document))) };
    if (e.seq !== i || e.prev_hash !== prevHash) throw new Error(`issuer history seq ${e.seq}: does not link`);
    if (toHex(sha256(canonical(m))) !== e.entry_hash) throw new Error(`issuer history seq ${e.seq}: entry hash does not recompute`);
    const pk = keyFromDocument(prevDoc ?? e.document, e.kid);
    const p = parseCompact(e.jws);
    if (p.header.kid !== e.kid || !verifyRaw(String(p.header.alg), pk, p.signingInput, p.signature))
      throw new Error(`issuer history seq ${e.seq}: signature does not verify`);
    if (JSON.stringify(canonical(payloadJson(p))) !== JSON.stringify(canonical(m)))
      throw new Error(`issuer history seq ${e.seq}: the signature covers another version`);
    prevHash = e.entry_hash; prevDoc = e.document;
    out.push({ time: e.version_time, document: e.document });
  });
  return out;
}

/** Verify compromise declarations against the update keys of the version each names. */
export function verifyCompromises(items: any[], logs: Record<string, Version[]>): Compromise[] {
  const out: Compromise[] = [];
  for (const it of items ?? []) {
    const p = parseCompact(it.jws);
    const body = payloadJson(p);
    const versions = logs[body.did];
    if (!versions) throw new Error(`compromise for ${body.did}: its log is not in the bundle`);
    const at = versions.find((v) => v.versionId === it.versionId);
    const kid = String(p.header.kid ?? "");
    const signer = kid.startsWith("did:key:") ? kid.split("#")[1] : "";
    if (p.header.typ !== COMPROMISE_TYP || !at || kid !== updateKeyVm(signer) || !at.updateKeys.includes(signer))
      throw new Error(`compromise for ${body.did}: not declared by an update key of its version`);
    if (!verifyRaw(String(p.header.alg), fromMultikey(signer), p.signingInput, p.signature))
      throw new Error(`compromise for ${body.did}: signature does not verify`);
    out.push({ did: body.did, kid: body.kid, compromisedAfter: body.compromisedAfter });
  }
  return out;
}

export class Resolver {
  logs: Record<string, Version[]> = {};
  histories: Record<string, { time: string; document: Record<string, any> }[]> = {};
  compromised: Compromise[] = [];
  errors: Record<string, string> = {};

  constructor(dids: Record<string, DidSource>, compromise: any[] = []) {
    for (const [did, src] of Object.entries(dids ?? {})) {
      try {
        if (Array.isArray(src)) this.logs[did] = verifyLog(src, did);
        else this.histories[did] = verifyIssuerHistory(src as IssuerHistory);
      } catch (e) { this.errors[did] = (e as Error).message; }
    }
    this.compromised = verifyCompromises(compromise, this.logs);
  }

  /** The document of `did` in force at `at` (ISO time). */
  documentAt(did: string, at: string): Record<string, any> {
    if (this.errors[did]) throw new Error(`${did}: ${this.errors[did]}`);
    if (this.logs[did]) return select(this.logs[did], { versionTime: at }).state;
    const h = this.histories[did];
    if (h) {
      const inForce = h.filter((v) => Date.parse(v.time) <= Date.parse(at));
      return (inForce.length ? inForce[inForce.length - 1] : h[0]).document;
    }
    throw new Error(`${did} is not in the bundle`);
  }

  /** The public key `kid` names, as it stood at `at`. */
  key(kid: string, at: string, relationship = "assertionMethod"): PublicKey {
    const did = kid.split("#")[0];
    for (const c of this.compromised)
      if (c.kid === kid && Date.parse(c.compromisedAfter) <= Date.parse(at))
        throw new Error(`${kid} was declared compromised after ${c.compromisedAfter}`);
    if (did.startsWith("did:key:")) {
      const pk = didKeyPublicKey(did);
      const frag = kid.split("#")[1];
      if (frag && frag !== did.slice("did:key:".length)) throw new Error(`${kid} is not this did:key's key`);
      return pk;
    }
    return keyFromDocument(this.documentAt(did, at), kid, relationship);
  }
}
